import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { FormService } from "../../../../../dynamic-forms/form.service";
import { DynamicInput } from "../../../../../dynamic-forms/inputs/dynamic-input.model";
import { Switch } from "../../../../../dynamic-forms/inputs/switch/switch.model";
import { TextboxType } from "../../../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../../../dynamic-forms/inputs/textbox/textbox.model";
import { LookupModel } from "../../../../../shared/models/lookup-model";
import { ArrayHelper } from "../../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../../utilities/contracts/string-helper";
import { ProductType } from "../../new-project/product-type.enum";
import { IOverreadModel } from "../overread.model";
import { IRateModel } from "../rates.model";

interface IOverreadForm {
  formGroup: FormGroup;
  models: DynamicInput[];
}

enum FormInputs {
  COMPLIANT = "compliant",
  PARTIAL_COMPLIANT = "partiallycompliant",
  NON_COMPLIANT = "noncompliant",
  EXCLUSION = "noncompliantexclusion",
}

enum OverreadFeedbackForm {
  OVERREAD = "overreadFeedbackEnabled",
}

enum ActiveFormInputs {
  ACTIVE = "active",
}

enum STATUS {
  INACTIVE,
  ACTIVE,
}

@Component({
             selector: "app-project-sampling-overread",
             templateUrl: "./project-sampling.component.html",
             styleUrls: ["./project-sampling.component.scss"],
           })
export class ProjectSamplingComponent implements OnInit {

  @Input() set overreadInfo(info: IOverreadModel) {
    if (!ArrayHelper.isAvailable(info?.overread?.rates?.rate) || !ArrayHelper.isAvailable(info?.overread2?.rates?.rate)) {
      this.patchDefaultValues("OR1");
      this.patchDefaultValues("OR2");
      return;
    }
    const overread1Info = info.overread.rates.rate.filter(r => !StringHelper.isAvailable(r?.measurecode));
    const overread2Info = info.overread2.rates.rate.filter(r => !StringHelper.isAvailable(r?.measurecode));
    this.patchValues(overread1Info, "OR1");
    this.patchValues(overread2Info, "OR2");
  }

  @Input() set overreadFeedback(value: number) {
    if (!NumberHelper.isAvailable(Number(value))) {
      return;
    }
    this.overreadFeedbackFormGroup?.patchValue({ [OverreadFeedbackForm.OVERREAD]: Number(value) });
  }

  @Input() projectLookupDetails: LookupModel;

  formOverread: IOverreadForm = {formGroup: undefined, models: []};
  formOverread2: IOverreadForm = {formGroup: undefined, models: []};
  activeFormGroup: IOverreadForm = {formGroup: undefined, models: []};
  active2FormGroup: IOverreadForm = {formGroup: undefined, models: []};
  overreadFeedbackFormGroup: FormGroup;
  overreadFeedbackSwitch = new Switch(
    {
      key: "overreadFeedbackEnabled",
      label: "",
    });

  constructor(private readonly formService: FormService) {
  }

  ngOnInit(): void {
    this.setFormControls();
    this.overreadFeedbackFormGroup = this.formService.createFormGroup([this.overreadFeedbackSwitch]);
    const activeSwitch = new Switch(
      {
        key: ActiveFormInputs.ACTIVE,
        label: "",
      });
    this.activeFormGroup.formGroup = this.formService.createFormGroup([activeSwitch]);
    this.active2FormGroup.formGroup = this.formService.createFormGroup([activeSwitch]);
    this.activeFormGroup.models = [activeSwitch];
    this.active2FormGroup.models = [activeSwitch];
  }

  get isHedisNextGen(): boolean {
    return this.projectLookupDetails.templateID === ProductType.FULL_WORKFLOW_V2025_NEWGEN || this.projectLookupDetails.templateID === ProductType.FULL_WORKFLOW_V2026_NEWGEN;
  }

  getControls(form: FormGroup): AbstractControl[] {
    const controls: AbstractControl[] = [];
    for (const [key, control] of Object.entries(form.controls)) {
      controls.push(control);
    }
    return controls;
  }

  trackByIndex(index: number): number {
    return index;
  }

  formHasErrors(): boolean {
    return this.formOverread.formGroup.invalid || this.formOverread2.formGroup.invalid;
  }

  getValuesAsObject(type: "OR1" | "OR2" = "OR1"): IRateModel[] {
    if (type === "OR1") {
      return [...this.getFormattedValues("OR1")];
    }
    return [...this.getFormattedValues("OR2")];
  }

  getOverreadFeedbackValue(): number {
    return Number(this.overreadFeedbackFormGroup.get(OverreadFeedbackForm.OVERREAD).value);
  }

  private getFormattedValues(type: "OR1" | "OR2" = "OR1"): IRateModel[] {
    if (type === "OR1") {
      return Object.values(FormInputs).map(v => {
        return {
          value: this.formOverread.formGroup.get(v)?.value as number,
          ratetype: v,
          active: Number(this.activeFormGroup.formGroup.get(ActiveFormInputs.ACTIVE).value),
        };
      });
    }
    return Object.values(FormInputs).map(v => {
      return {
        value: this.formOverread2.formGroup.get(v)?.value as number,
        ratetype: v,
        active: Number(this.active2FormGroup.formGroup.get(ActiveFormInputs.ACTIVE).value),
      };
    });
  }

  private setFormControls(): void {
    const models: DynamicInput[] = [];
    Object.values(FormInputs).forEach((input: string) => {
      models.push(this.createTextbox(input, ""));
    });
    this.formOverread = {
      formGroup: this.formService.createFormGroup([...models]),
      models,
    };
    this.formOverread2 = {
      formGroup: this.formService.createFormGroup([...models]),
      models,
    };
  }

  private createTextbox(key: string, label: string): Textbox {
    return new Textbox(
      {
        key,
        label,
        validators: [Validators.min(0), Validators.max(100), Validators.required],
        type: TextboxType.NUMBER,
      });
  }

  private patchValues(arr: IRateModel[], type: "OR1" | "OR2" = "OR1"): void {
    if (!ArrayHelper.isAvailable(arr)) {
      this.patchDefaultValues(type);
      return;
    }
    const mapToObjectArray = arr.map(rate => {
      return { [rate.ratetype]: rate.value };
    });
    const patchObject = Object.assign({}, ...mapToObjectArray);
    const isActive = arr.some(e => e.active === STATUS.ACTIVE);
    if (type === "OR1") {
      this.formOverread.formGroup.patchValue(patchObject);
      this.activeFormGroup.formGroup.patchValue({ [ActiveFormInputs.ACTIVE]: isActive ? 1 : 0 });
      return;
    }
    this.formOverread2.formGroup.patchValue(patchObject);
    this.active2FormGroup.formGroup.patchValue({ [ActiveFormInputs.ACTIVE]: isActive ? 1 : 0 });
  }

  private patchDefaultValues(type: "OR1" | "OR2" = "OR1"): void {
    if (type === "OR1") {
      const patchValuesOR1 = {
        compliant: 10,
        partiallycompliant: 10,
        noncompliant: 10,
        noncompliantexclusion: 100,
      };
      this.formOverread.formGroup?.patchValue(patchValuesOR1);
      this.activeFormGroup.formGroup?.patchValue({ [ActiveFormInputs.ACTIVE]: 0 });
      return;
    }
    const patchValuesOR2 = {
      compliant: 0,
      partiallycompliant: 0,
      noncompliant: 0,
      noncompliantexclusion: 0,
    };
    this.formOverread2.formGroup?.patchValue(patchValuesOR2);
    this.active2FormGroup.formGroup?.patchValue({ [ActiveFormInputs.ACTIVE]: 0 });
  }

}
